import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import NotificationPopup from "@/components/NotificationPopup";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LogOut, Menu } from "lucide-react";

export default function DashboardLayout({ nav = [], title, subtitle, children, roleLabel }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const NavList = ({ onNavigate }) => (
    <>
      {nav.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={onNavigate}
          data-testid={`nav-${n.key}`}
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
              isActive
                ? "side-active text-white font-medium"
                : "text-white/70 hover:text-white hover:bg-white/5"
            }`
          }
        >
          {n.icon ? <n.icon className="h-4 w-4" /> : null}
          <span>{n.label}</span>
        </NavLink>
      ))}
    </>
  );

  const SidebarInner = ({ onNavigate }) => (
    <div className="h-full flex flex-col text-white bg-sidebar-gradient relative overflow-hidden">
      <div className="absolute inset-0 grain opacity-20 pointer-events-none" />
      <div className="px-5 py-5 border-b border-white/10 relative">
        <Logo size="sm" invert />
        <div className="mt-3 text-[10px] uppercase tracking-widest text-[#f57320] font-semibold">
          {roleLabel}
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 relative overflow-y-auto">
        <NavList onNavigate={onNavigate} />
      </nav>
      <div className="p-3 border-t border-white/10 relative">
        <div className="px-2 py-2 mb-2">
          <div className="text-sm font-medium truncate text-white">{user?.name}</div>
          <div className="text-xs text-white/60 truncate font-mono">{user?.mobile}</div>
        </div>
        <Button
          data-testid="logout-btn"
          variant="outline"
          className="w-full bg-white/5 border-white/20 text-white hover:bg-white hover:text-[#1a3ba8]"
          onClick={handleLogout}
        >
          <LogOut className="h-4 w-4 mr-2" /> Logout / लॉग आउट
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-hero-gradient flex">
      <NotificationPopup />

      {/* Desktop sidebar */}
      <aside className="w-64 sticky top-0 h-screen hidden md:block">
        <SidebarInner />
      </aside>

      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-10 bg-white/85 backdrop-blur-xl border-b">
          <div className="px-4 md:px-8 py-4 flex items-center justify-between gap-3">
            {/* Mobile hamburger */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="md:hidden"
                  data-testid="mobile-menu-btn"
                  aria-label="Open menu"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72 border-0 bg-transparent" data-testid="mobile-menu-panel">
                <SidebarInner onNavigate={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>
            <div className="flex-1 min-w-0">
              <h1 className="font-display text-xl md:text-2xl font-semibold tracking-tight truncate">{title}</h1>
              {subtitle && <p className="text-sm text-slate-500 mt-0.5 truncate">{subtitle}</p>}
            </div>
          </div>
        </header>
        <div className="px-4 md:px-8 py-6">{children}</div>
      </main>
    </div>
  );
}

const ACCENT_STYLE = {
  blue:   { grad: "bg-brand-gradient",    text: "text-[#1a3ba8]", ring: "ring-[#1a3ba8]/10" },
  orange: { grad: "bg-orange-gradient",   text: "text-[#f57320]", ring: "ring-[#f57320]/10" },
  green:  { grad: "bg-emerald-gradient",  text: "text-emerald-600", ring: "ring-emerald-200/40" },
  red:    { grad: "bg-rose-gradient",     text: "text-rose-600", ring: "ring-rose-200/40" },
  violet: { grad: "bg-violet-gradient",   text: "text-violet-600", ring: "ring-violet-200/40" },
  sky:    { grad: "bg-sky-gradient",      text: "text-sky-600", ring: "ring-sky-200/40" },
};

export function KpiCard({ label, value, hint, accent = "blue", icon: Icon, testId }) {
  const s = ACCENT_STYLE[accent] || ACCENT_STYLE.blue;
  return (
    <div className={`stat-card bg-white border rounded-xl p-5 relative overflow-hidden ring-1 ${s.ring}`} data-testid={testId}>
      <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full ${s.grad} opacity-10`} />
      <div className="flex items-start justify-between relative">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">{label}</div>
          <div className={`font-display font-semibold text-3xl mt-2 ${s.text}`}>{value}</div>
          {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
        </div>
        {Icon && (
          <div className={`w-10 h-10 rounded-lg ${s.grad} text-white flex items-center justify-center shadow-md`}>
            <Icon className="h-5 w-5" />
          </div>
        )}
      </div>
    </div>
  );
}
